import React, { useState, useEffect } from 'react';
import { DollarSign, Coins, CreditCard, Banknote, CheckCircle, Clock, Plus, Receipt, UserCheck } from 'lucide-react';
import { toast } from 'react-toastify';
import { useTipsReport } from '../../hooks/useReports';
import { settleTips, getTipSettlements, TipSettlement } from '../../services/ReportsService';
import { Button } from '../../components/ui';

interface Props {
  from: string;
  to: string;
}

function formatCOP(val: number) {
  return '$ ' + Math.round(val).toLocaleString('es-CO');
}

export function TipsTab({ from, to }: Props) {
  const { data, isLoading, refetch } = useTipsReport(from, to);
  const [settlements, setSettlements] = useState<TipSettlement[]>([]);
  const [loadingSettlements, setLoadingSettlements] = useState(false);

  // Modal para liquidar propinas
  const [showModal, setShowModal] = useState(false);
  const [settleAmount, setSettleAmount] = useState<number>(0);
  const [paymentMethod, setPaymentMethod] = useState<string>('EFECTIVO');
  const [notes, setNotes] = useState<string>('');
  const [saving, setSaving] = useState(false);

  const loadSettlements = async () => {
    try {
      setLoadingSettlements(true);
      const list = await getTipSettlements();
      setSettlements(list);
    } catch (err) {
      console.warn('Error loading tip settlements:', err);
    } finally {
      setLoadingSettlements(false);
    }
  };

  useEffect(() => {
    loadSettlements();
  }, []);

  const handleOpenSettle = (suggestedAmount?: number) => {
    setSettleAmount(suggestedAmount !== undefined ? suggestedAmount : data?.pendingToSettle ?? 0);
    setNotes(`Liquidación de propinas del período ${from} a ${to}`);
    setShowModal(true);
  };

  const handleSaveSettle = async (e: React.FormEvent) => {
    e.preventDefault();
    if (settleAmount <= 0) {
      toast.error('El monto a liquidar debe ser mayor a 0');
      return;
    }
    setSaving(true);
    try {
      await settleTips({
        amount: settleAmount,
        payment_method: paymentMethod,
        notes: notes || undefined,
      });
      toast.success('Liquidación de propinas registrada exitosamente');
      setShowModal(false);
      refetch();
      loadSettlements();
    } catch (err: any) {
      toast.error(err?.response?.data?.message || 'Error al liquidar propinas');
    } finally {
      setSaving(false);
    }
  };

  if (isLoading) {
    return (
      <div className="py-20 text-center">
        <div className="mx-auto mb-3 h-8 w-8 animate-spin rounded-full border-2 border-primary border-t-transparent" />
        <p className="text-sm font-medium text-text-muted">Calculando balance de propinas y liquidaciones...</p>
      </div>
    );
  }

  const totalTips = data?.totalTips ?? 0;
  const cashTips = data?.cashTips ?? 0;
  const electronicTips = data?.electronicTips ?? 0;
  const totalSettled = data?.totalSettled ?? 0;
  const pendingToSettle = data?.pendingToSettle ?? 0;
  const byWaiter = data?.byWaiter ?? [];

  return (
    <div className="space-y-6">
      {/* ── Banner y Botón de Liquidación ── */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 rounded-3xl border border-primary/20 bg-gradient-to-r from-emerald-500/10 via-emerald-500/5 to-transparent p-5 sm:p-6 shadow-sm">
        <div>
          <div className="flex items-center gap-2">
            <span className="rounded-lg bg-emerald-500/20 p-1.5 text-emerald-500">
              <Coins className="h-4 w-4" />
            </span>
            <h2 className="text-base font-bold text-text">Control y Liquidación de Propinas</h2>
          </div>
          <p className="text-xs text-text-muted mt-1 max-w-xl">
            Gestiona la recaudación de propinas en efectivo y datáfono/transferencia, y registra las entregas formales al personal para mantener cuentas claras y sin disputas.
          </p>
        </div>

        <Button
          variant="primary"
          size="md"
          onClick={() => handleOpenSettle()}
          className="rounded-2xl flex items-center gap-2 shadow-md hover:scale-[1.02] transition-transform"
        >
          <Plus className="h-4 w-4" />
          Liquidar Propinas
        </Button>
      </div>

      {/* ── Tarjetas de Estado Financiero de Propinas ── */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
        {/* Total Recaudado */}
        <div className="rounded-3xl border border-border bg-surface p-4 sm:p-5 shadow-sm">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs font-bold uppercase tracking-wider text-text-muted">Total Propinas</span>
            <span className="rounded-lg bg-primary/10 p-1.5 text-primary">
              <Coins className="h-4 w-4" />
            </span>
          </div>
          <p className="text-xl sm:text-2xl font-extrabold text-text">{formatCOP(totalTips)}</p>
          <p className="text-[11px] text-text-muted mt-1">Acumulado en el período</p>
        </div>

        {/* Efectivo vs Datáfono */}
        <div className="rounded-3xl border border-border bg-surface p-4 sm:p-5 shadow-sm">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs font-bold uppercase tracking-wider text-text-muted">Por Medio</span>
            <span className="rounded-lg bg-blue-500/10 p-1.5 text-blue-500">
              <CreditCard className="h-4 w-4" />
            </span>
          </div>
          <div className="space-y-1">
            <div className="flex justify-between text-xs">
              <span className="text-text-muted flex items-center gap-1">
                <Banknote className="h-3 w-3 text-emerald-500" /> Efectivo:
              </span>
              <span className="font-bold text-text">{formatCOP(cashTips)}</span>
            </div>
            <div className="flex justify-between text-xs">
              <span className="text-text-muted flex items-center gap-1">
                <CreditCard className="h-3 w-3 text-blue-500" /> Electrónico:
              </span>
              <span className="font-bold text-text">{formatCOP(electronicTips)}</span>
            </div>
          </div>
        </div>

        {/* Total Ya Liquidado */}
        <div className="rounded-3xl border border-border bg-surface p-4 sm:p-5 shadow-sm">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs font-bold uppercase tracking-wider text-text-muted">Entregado / Pagado</span>
            <span className="rounded-lg bg-emerald-500/10 p-1.5 text-emerald-500">
              <CheckCircle className="h-4 w-4" />
            </span>
          </div>
          <p className="text-xl sm:text-2xl font-extrabold text-emerald-600 dark:text-emerald-400">
            {formatCOP(totalSettled)}
          </p>
          <p className="text-[11px] text-text-muted mt-1">Liquidado a colaboradores</p>
        </div>

        {/* Saldo Pendiente */}
        <div className="rounded-3xl border border-border bg-surface p-4 sm:p-5 shadow-sm">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs font-bold uppercase tracking-wider text-text-muted">Por Liquidar</span>
            <span className="rounded-lg bg-amber-500/10 p-1.5 text-amber-500">
              <Clock className="h-4 w-4" />
            </span>
          </div>
          <p className="text-xl sm:text-2xl font-extrabold text-amber-500">
            {formatCOP(pendingToSettle)}
          </p>
          <p className="text-[11px] text-text-muted mt-1">Disponible para entrega</p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* ── Distribución por Mesero ── */}
        <div className="rounded-3xl border border-border bg-surface p-5 shadow-sm">
          <h3 className="text-sm font-bold text-text mb-1">Propinas Generadas por Colaborador</h3>
          <p className="text-xs text-text-muted mb-4">
            Total recaudado por pedidos atendidos por cada mesero o canal
          </p>

          {byWaiter.length === 0 ? (
            <p className="py-8 text-center text-xs text-text-muted">No hay propinas registradas</p>
          ) : (
            <div className="space-y-3">
              {byWaiter.map((bw, idx) => {
                const pct = totalTips > 0 ? Math.round((bw.amount / totalTips) * 100) : 0;
                return (
                  <div key={idx} className="rounded-2xl border border-border/70 bg-surface-muted/30 p-3">
                    <div className="flex items-center justify-between text-xs mb-1.5">
                      <div className="flex items-center gap-2">
                        <span className="h-2 w-2 rounded-full bg-primary" />
                        <span className="font-bold text-text">{bw.waiter}</span>
                        <span className="text-[10px] text-text-muted">({bw.count} pagos con propina)</span>
                      </div>
                      <div className="text-right">
                        <span className="font-extrabold text-text">{formatCOP(bw.amount)}</span>
                        <span className="text-[10px] text-text-muted ml-1.5">({pct}%)</span>
                      </div>
                    </div>
                    {/* Barra de progreso */}
                    <div className="h-2 w-full overflow-hidden rounded-full bg-border">
                      <div className="h-full bg-primary rounded-full transition-all" style={{ width: `${pct}%` }} />
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* ── Historial de Liquidaciones Formales ── */}
        <div className="rounded-3xl border border-border bg-surface p-5 shadow-sm flex flex-col justify-between">
          <div>
            <h3 className="text-sm font-bold text-text mb-1">Últimas Liquidaciones Registradas</h3>
            <p className="text-xs text-text-muted mb-4">
              Registro histórico de entregas de propina con fecha y firma
            </p>

            {settlements.length === 0 ? (
              <div className="py-12 text-center text-xs text-text-muted">
                No hay liquidaciones registradas todavía. Usa el botón "Liquidar Propinas" para asentar la primera entrega.
              </div>
            ) : (
              <div className="space-y-2.5 max-h-[340px] overflow-y-auto pr-1">
                {settlements.slice(0, 8).map((st) => (
                  <div
                    key={st.id}
                    className="flex items-center justify-between rounded-2xl bg-surface-muted/40 border border-border/70 p-3 text-xs"
                  >
                    <div>
                      <div className="flex items-center gap-1.5">
                        <span className="font-bold text-text">
                          {st.user?.name ?? 'Pozo Común del Turno'}
                        </span>
                        <span className="rounded bg-surface border border-border px-1.5 py-0.5 text-[9px] font-semibold text-text-muted uppercase">
                          {st.payment_method}
                        </span>
                      </div>
                      <p className="text-[11px] text-text-muted mt-0.5">
                        {new Date(st.settled_at).toLocaleDateString()} {new Date(st.settled_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })} • Por: {st.settledByUser?.name ?? 'Admin'}
                      </p>
                      {st.notes && (
                        <p className="text-[10px] text-text-muted italic mt-0.5">"{st.notes}"</p>
                      )}
                    </div>
                    <span className="font-extrabold text-sm text-emerald-600 dark:text-emerald-400">
                      {formatCOP(st.amount)}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* ── Modal de Liquidación Formal ── */}
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-in fade-in duration-200">
          <div className="relative w-full max-w-md overflow-hidden rounded-3xl border border-border bg-surface p-6 shadow-2xl">
            <div className="flex items-center gap-2 mb-4 border-b border-border pb-3">
              <span className="rounded-lg bg-primary/10 p-2 text-primary">
                <Coins className="h-5 w-5" />
              </span>
              <div>
                <h3 className="text-base font-bold text-text">Registrar Liquidación de Propinas</h3>
                <p className="text-xs text-text-muted">Asienta la entrega física o transferencia al personal</p>
              </div>
            </div>

            <form onSubmit={handleSaveSettle} className="space-y-4">
              <div>
                <label className="block text-xs font-semibold text-text mb-1">Monto a Liquidar ($ COP)</label>
                <input
                  type="number"
                  min="1"
                  step="100"
                  value={settleAmount}
                  onChange={(e) => setSettleAmount(Number(e.target.value))}
                  className="w-full rounded-2xl border border-border bg-surface px-4 py-2.5 text-base font-bold text-text focus:border-primary focus:outline-none"
                  required
                />
                <p className="text-[11px] text-text-muted mt-1">
                  Saldo pendiente estimado: {formatCOP(pendingToSettle)}
                </p>
              </div>

              <div>
                <label className="block text-xs font-semibold text-text mb-1">Método de Entrega</label>
                <select
                  value={paymentMethod}
                  onChange={(e) => setPaymentMethod(e.target.value)}
                  className="w-full rounded-2xl border border-border bg-surface px-4 py-2 text-sm text-text focus:border-primary focus:outline-none"
                >
                  <option value="EFECTIVO">Efectivo de Caja</option>
                  <option value="TRANSFERENCIA">Transferencia Bancaria / Nequi</option>
                  <option value="CHEQUE">Cheque / Otro</option>
                </select>
              </div>

              <div>
                <label className="block text-xs font-semibold text-text mb-1">Notas / Observaciones</label>
                <textarea
                  rows={2}
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="Ej: Reparto de propinas fin de semana entre cocina y meseros"
                  className="w-full rounded-2xl border border-border bg-surface p-3 text-xs text-text focus:border-primary focus:outline-none"
                />
              </div>

              <div className="flex items-center justify-end gap-2 pt-2 border-t border-border">
                <Button
                  type="button"
                  variant="ghost"
                  size="md"
                  onClick={() => setShowModal(false)}
                  disabled={saving}
                  className="rounded-2xl"
                >
                  Cancelar
                </Button>
                <Button
                  type="submit"
                  variant="primary"
                  size="md"
                  disabled={saving}
                  className="rounded-2xl font-bold"
                >
                  {saving ? 'Guardando...' : 'Confirmar Liquidación'}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
